import { Component, Input, Output, EventEmitter, ViewChild, TemplateRef, OnDestroy } from "@angular/core";
import { IIndicator, IUIError } from "@gtmhub/error-handling";
import { localize } from "@gtmhub/localization";
import { Ng1StateProxy } from "@gtmhub/core/routing";
import { ITeam, IBulkActionOnTeamsResponse } from "@gtmhub/teams";
import { IUser, IBulkActionOnUsersResponse } from "@gtmhub/users";
import { TracingService } from "@webapp/core/tracing/services/tracing.service";
import { ActionType, EntityType } from "@webapp/teams/models/status-actions.models";
import { TeamsFacade } from "@webapp/teams/services/teams-facade.service";
import { UiModalRef } from "@webapp/ui/modal/abstracts/modal-ref";
import { UiModalService } from "@webapp/ui/modal/services/modal.service";
import { UiNotificationService } from "@webapp/ui/notification/services/notification.service";
import { UsersFacade } from "@webapp/users/services/users-facade.service";
import { Observable, Subscription, throwError, map, take } from "rxjs";
import { ConfirmStatusActionComponent } from "../confirm-status-action/confirm-status-action.component";
import { AnalyticsService } from "@gtmhub/core/analytics";

@Component({
  selector: "gh-status-action",
  templateUrl: "./status-action.component.html",
})
export class StatusActionComponent implements OnDestroy {
  @ViewChild("notificationTemplate")
  public notificationTemplate: TemplateRef<object>;
  @ViewChild("usersLimitTemplate")
  public usersLimitTemplate: TemplateRef<object>;

  @Input() public entity: IUser | ITeam;
  @Input() public entityType: EntityType;
  @Input() public actionType: ActionType;
  @Input() public openDetailsPath: {
    path: string;
    paramName: string;
  };

  @Output() public onSuccess: EventEmitter<object> = new EventEmitter();

  get actionKey(): string {
    return `${this.actionType}_${this.entityType}`;
  }

  get viewEntityKey(): string {
    return this.entityType === "team" ? "view_team" : "view_profile";
  }

  get statusChangedTextKey(): string {
    return {
      deactivate_team: "team_x_deactivated",
      activate_team: "team_x_activated",
      delete_team: "team_x_deleted",
      deactivate_user: "user_x_deactivated",
      activate_user: "user_x_activated",
      delete_user: "user_x_deleted",
    }[`${this.actionType}_${this.entityType}`];
  }

  public indicators: { loading?: IIndicator } = {};

  public confirmationModal: UiModalRef<ConfirmStatusActionComponent>;
  public usersLimitReachedModal: UiModalRef<TemplateRef<object>>;
  public modalSubscriptions: Subscription;
  public notificationId: string;

  constructor(
    private ng1StateProxy: Ng1StateProxy,
    private modalService: UiModalService,
    private teamsFacade: TeamsFacade,
    private usersFacade: UsersFacade,
    private notificationsService: UiNotificationService,
    private tracingService: TracingService,
    private analyticsService: AnalyticsService
  ) {}

  public ngOnDestroy(): void {
    this.destroyModals();
  }

  public confirmAction(): void {
    this.destroyModals();

    if (this.actionKey == "activate_user" && (this.entity as IUser).subscriptionType !== "restricted") {
      this.checkUsersLimitReached();
    } else {
      this.openConfirmationModal();
    }
  }

  private checkUsersLimitReached(): void {
    this.usersFacade
      .getUsersLimit$(1)
      .pipe(take(1))
      .subscribe({
        next: (payload) => {
          const maximumUsersLimitReached = payload.hasLimitSet && payload.isLimitReached;
          if (maximumUsersLimitReached) {
            this.openUsersLimitReachedWarningModal();
          } else {
            this.openConfirmationModal();
          }
        },
        error: (error) => {
          this.indicators.loading = { error };
        },
      });
  }

  private openConfirmationModal(): void {
    this.confirmationModal = this.modalService.create({
      uiContent: ConfirmStatusActionComponent,
      uiFooter: null,
      uiClosable: false,
      uiComponentParams: {
        entity: this.entity,
        entityType: this.entityType,
        actionType: this.actionType,
        indicators: this.indicators,
      },
    });

    this.modalSubscriptions = this.confirmationModal.componentInstance.confirm.subscribe(() => this.performAction());
    this.modalSubscriptions.add(this.confirmationModal.componentInstance.close.subscribe(() => this.destroyModals()));
  }

  private openUsersLimitReachedWarningModal(): void {
    this.usersLimitReachedModal = this.modalService.create({
      uiContent: this.usersLimitTemplate,
      uiFooter: null,
    });
  }

  public contactUs(): void {
    this.analyticsService.contactSales("contactSales");
    this.destroyModals();
  }

  public openEntityDetails(): void {
    const { path, paramName } = this.openDetailsPath;
    this.ng1StateProxy.go(path, { [paramName]: this.entity.id });

    this.notificationsService.remove(this.notificationId);
  }

  public performAction(): void {
    this.indicators.loading = { progress: true };

    this.tracingService.traceAction(this.actionKey, () =>
      this.perform()
        .pipe(take(1))
        .subscribe({
          next: (payload) => {
            if (payload.hasFailures) {
              const error: IUIError = {
                body: `An error occured while trying to ${this.actionType} the ${this.entityType}.`,
                type: "bad_request",
              };
              this.indicators.loading = { error };
              return;
            }

            this.removeInidicator();
            this.destroyModals();
            this.onSuccess.emit({ ...payload, action: this.actionType });
            this.showSuccessNotification();
          },
          error: (error) => {
            this.indicators.loading = { error };
          },
        })
    );
  }

  public destroyModals(): void {
    if (this.modalSubscriptions) {
      this.modalSubscriptions.unsubscribe();
      this.modalSubscriptions = null;
    }

    if (this.confirmationModal) {
      this.confirmationModal.close();
      this.confirmationModal = null;
    }

    if (this.usersLimitReachedModal) {
      this.usersLimitReachedModal.close();
      this.usersLimitReachedModal = null;
    }
  }

  private removeInidicator(): void {
    delete this.indicators.loading;
  }

  private perform(): Observable<IBulkActionOnUsersResponse | IBulkActionOnTeamsResponse> {
    switch (this.actionKey) {
      case "activate_user":
        return this.usersFacade.activateUser$(this.entity);
      case "deactivate_user":
        return this.usersFacade.deactivateUser$(this.entity);
      case "delete_user":
        return this.usersFacade.deleteUser$(this.entity);
      case "activate_team":
        return this.teamsFacade.activateTeam$(this.entity);
      case "deactivate_team":
        return this.teamsFacade.deactivateTeam$(this.entity);
      case "delete_team":
        return this.teamsFacade.deleteTeam$(this.entity).pipe(map(() => ({ hasFailures: false })));
      default:
        return throwError(() => ({ title: `Unknown action type ${this.actionType} for entity ${this.entityType}` }));
    }
  }

  private showSuccessNotification(): void {
    const notification = this.notificationsService.template(this.notificationTemplate, {
      notificationPlacement: "bottomRight",
      // provide the current action-related texts as context variables as the notification is opened asynchronously
      // and a new value for this.actionType is already provided to this component when the notification is rendered,
      // resulting in a wrong value being displayed if this.actionType is read from the component
      notificationData: {
        actionType: this.actionType,
        statusChangedText: localize(this.statusChangedTextKey, { name: this.entity.name }),
      },
    });
    this.notificationId = notification.messageId;
  }
}
